// ViewerChrome — Phase 11 Step 4 Block 4 + Step 5 Block 1.
//
// Fullscreen-Viewer für `/s/:slug`. Immer dunkel (unabhängig von Variant),
// Top-Bar (Close + Creator + Mode-Toggle), Right-Rail (Like/Save/Share,
// variant-aware mit Branding-Accent), Colophon (Creator-Block + Tags +
// "Weitere Scrollys"), ESC-Close.
//
// Mode (Step 5 Block 1):
//   - `phone`      → Center-Letterbox mit Phone-Frame, Rail daneben (rechts).
//   - `immersive`  → Content füllt Viewport, Rail floatet als Overlay rechts.
// Der Toggle ist in Block 1 lokaler State (Default `phone`) — Block 2 ersetzt
// das mit einem `useViewerMode()`-Hook (URL + localStorage).
//
// Source-Pfade (Phase 11.5):
//   - `native`   → Platzhalter-Div-Content + Branding-Pipeline an.
//   - `embed`    → Iframe zu `externalUrl` mit Sandbox; Branding-Pipeline aus.
//   - `imported` → Platzhalter-Div wie native, Branding-Pipeline aus.
//
// Optimistic-UI:
//   Like/Save/Share/Follow sind lokaler useState — kein Backend, keine
//   Persistenz. Wenn `loggedIn=false` öffnet Klick ein Sign-in-Modal-Stub.

import { seitenScrollNutzbar } from '../../lib/seiten-scroll';
import { useEffect, useRef, useState } from 'react';
import {
  IconHeart,
  IconBookmark,
  IconShare,
  IconX,
  IconMaximize,
  IconMinimize,
} from '../common/Icons';
import type { CoverTextPosition } from "@scrolly/branding";
import { formatViews } from '../../lib/format';
import { type MockScrolly } from '../../lib/mock/scrollys';
// H1-Phase-1-Track-2-Sub-2.3 (2026-05-18): UpNext-Section aus Colophon
// raus, eigene Component nach Colophon. Source-of-truth: mockup
// docs/mockups/viewer.jsx UpNextInline.
import { UpNextInline } from './UpNextInline';
import type { ViewerMode } from '../../hooks/useViewerMode';
import { useViewerKeyboard } from '../../hooks/useViewerKeyboard';
import {
  useResponsiveViewer,
  FULLSCREEN_QUERY,
  ScrollyViewer,
  CoverSection,
  useCustomFontLoader,
  viewerT as t,
} from '@scrolly/viewer';
import type { Lang, FontInfoDto } from '@scrolly/viewer';
import type { CtaAbgabe, FontMapping } from '@scrolly/post-types';
import { resolveStartButtonImage, resolveStartButtonStyle, resolveStartTypography } from '@scrolly/post-types';
import { isInAppWebView } from '../../lib/in-app-webview';
import { istPortalReferrer, kamAusDemPortal, merkePortalHerkunft, PORTAL_HOME_URL } from '../../lib/herkunft-portal';
// Plan-N2 Block 3 v4 (2026-05-25 Bene): ClientOnly entfernt — app/-
// Pattern direkt SSR-rendern, kein PlaceholderContent-Flash mehr.
import { useResumeState } from '../../hooks/useResumeState';
import { publishReaderProgress, clearReaderProgress } from '../../lib/reader-progress';
import { usePublicAuth } from '../../hooks/usePublicAuth';
import { CompanyLogo } from './CompanyLogo';
import { Colophon, ColophonLegalFooter } from '@scrolly/viewer';
import { ScrollIndicatorEcho } from './ScrollIndicatorEcho';
import { VideoDebugOverlay } from './VideoDebugOverlay';
import { ScrollSprungOverlay } from './ScrollSprungOverlay';
import { useEngagementMutation } from '../../hooks/useEngagementMutation';
import { SignInModal } from '../auth/SignInModal';
import type { EngagementCounts } from '../../lib/api/client';
import { engagementVisibility } from '../../lib/engagement-visibility';

function readLiveViewportHeight(): number {
  if (typeof window === 'undefined') return 800;
  return window.visualViewport?.height ?? window.innerHeight;
}

// ---- Mode --------------------------------------------------------------
// Phase 11 Step 5: zwei Kern-Modes des Viewers. Phone ist der Step-4-Default
// (Letterbox + Rail daneben). Immersive füllt den Viewport; die Rail floatet
// als Overlay. Die Source-of-Truth für den Typ liegt im Hook — hier nur
// der Re-Export für Konsumenten, die ViewerChrome direkt importieren.

export type { ViewerMode };

// ---- V2 Branding CSS Vars (Phase 13 Commit 13) -------------------------
// The BrandingProvider wrapper (public/src/components/viewer/BrandingProvider.tsx)
// emits these custom properties onto the viewer subtree. Every chrome
// component that previously read the V1 `branding` prop now references
// these with an inline fallback — so the viewer stays readable when no
// provider is mounted (dev paths, storybook, bare component tests).
//
// The fallback colour matches VIEWER_BRANDING_DEFAULTS (platform pink),
// the radius matches the former V1 default (24px), the font stack matches
// what tokensToCssVars produces for DM Sans.
//
// H1-Phase-1.B (2026-05-18): uiFont-Fallback Inter → DM Sans, so dass
// Fallback und Branding-Provider-Output identisch sind (Drift-Elimination).
const BRAND_VAR = {
  accent: 'var(--scrolly-brand-colors-primary, rgba(255, 51, 102, 1))',
  accentContrast: '#111',
  radius: 'var(--scrolly-brand-shape-corner-radius, 24px)',
  uiFont:
    'var(--scrolly-brand-typography-ui-family, "DM Sans", -apple-system, system-ui, sans-serif)',
} as const;

// Stufe-1 i18n: alle Orgs → 'de'. Stufe 2 wird das per-scrolly auflösen
// via scrolly.metadata.lang ?? org.defaultLanguage ?? 'de'.
const LANG: Lang = 'de';

// ---- Auth-State (Sub-Step 3.3) ----------------------------------------
// Vorher: useMockLoggedIn — localStorage-stub für Dev-Browser. Ersetzt
// durch usePublicAuth (echte OIDC-Lesung via react-oidc-context). Während
// SSR + first-render returnt usePublicAuth anonyme defaults, nach
// explizitem User-Sign-In (Heart/Save → SignInModal) gibt es echten State.
// 2026-05-28: AutoSilentSso entfernt (Safari ITP iframe-Banner), kein
// Auto-Carry mehr von portal/ → viewer.
//
// Phase 1 (Sub-Auftrag 2 S2.6): Hook ist da, Behavior unverändert sichtbar
// (loggedIn=false bei anonym, Counter zeigt heute Mock-Werte).
// Phase 2 wird die SideActions erweitern (counts immer sichtbar, anonym
// → SignIn-Modal, auth → Like/Save mit hasLiked/hasSaved-State).

// ---- Hauptkomponente ---------------------------------------------------

export function ViewerChrome({
  scrolly,
  mode: modeProp,
  onToggleMode: onToggleModeProp,
  renderMode,
  chromeMinimal = false,
  onCtaAntwort,
  handyAnsicht = false,
  seitenScrollTest = false,
  imIframe = false,
  initialSceneIdxOneIndexed,
  publicKey,
  engagement,
  initialIsMobile = false,
  inAppWebView: inAppWebViewProp,
  orgCoverTextPosition = null,
}: {
  scrolly: MockScrolly;
  /**
   * In-App-WebView-Erkennung, SSR-seitig aus dem Request-User-Agent bestimmt
   * (ScrollyViewerPage, passToClient). Wird bevorzugt genutzt, damit Server und
   * Client dasselbe Ergebnis rendern → kein X-Blitz nach der Hydration. Fehlt
   * die Prop (Mock-/Alt-Pfade), faellt es auf navigator.userAgent zurueck.
   */
  inAppWebView?: boolean;
  /**
   * Plan-N2 Block 3 v3 (2026-05-25 Bene): SSR-UA-Hint für Mobile-
   * Hydration-Flash-Fix. ScrollyViewerPage liest pageContext.viewportHint
   * (gesetzt im onRenderHtml via UA-Sniffing) und passt es hier durch.
   * useResponsiveViewer nimmt's als Initial-State — kein PhoneStage-
   * Flash mehr beim ersten Render auf Mobile.
   */
  initialIsMobile?: boolean;
  /** W2 — Org-weite Cover-Anordnung aus dem Branding. `null` = nicht gesetzt. */
  orgCoverTextPosition?: CoverTextPosition | null;
  /**
   * Sub-Step 3.3 Phase 2: echter publicKey aus dem Backend-Resolver
   * (ResolvedScrolly.publicKey). Brauche ich für POST /like + /save +
   * /view-Endpoint-URLs. Optional weil Mock-Fallback-Pfade (lokales
   * SCROLLYS_BY_SLUG) keinen publicKey haben — dann blocken die
   * Engagement-Aktionen im SideActions silent.
   */
  publicKey?: string;
  /**
   * Sub-Step 3.3 Phase 2: Engagement-State (counts + optional hasLiked/
   * hasSaved). Wird via Prop von ScrollyViewerPage durchgereicht und
   * an SideActions weitergegeben. Bei Mock-Pfaden (kein Backend-Resolve)
   * kann das Feld fehlen — dann zeigt SideActions Nullen.
   */
  engagement?: EngagementCounts;
  /** Optional gesteuerter Mode. Ohne Prop fällt die Komponente auf lokalen
   * State zurück — das ist der Block-1-Pfad. Block 2 wird den Hook
   * (`useViewerMode()`) auf der Page-Ebene mounten und beides hier
   * durchreichen. */
  mode?: ViewerMode;
  onToggleMode?: () => void;
  /** Variante D (Vorbedingungs-Track STOPP D CP2): wenn `"og"` gesetzt
   * ist, blendet ViewerChrome alle UI-Chrome-Elemente aus (TopBar,
   * Colophon, KeyboardHint, SignInStub) und forciert immersive Mode.
   * Wird vom OG-Image-Backend (CP3) via `?renderMode=og` Query-Param
   * gesetzt, damit der Headless-Browser ein sauberes Vollbild rendern
   * kann.
   *
   * 2026-05-29: Plus "embedded" für iframe-Overlay aus portal/. Strippt
   * NUR TopBar (X-Button + Logo kollidieren mit Parent-Overlay-X).
   * SideActions + OverflowActionsMenu bleiben sichtbar — Heart/Save/Share
   * kommen aus iframe-eigener 3-Punkte-Nav statt Overlay-Parallel-Button
   * (Bene-Befund 2026-05-29 nach Polish-Sprint). CompanyLogo/Credits/
   * TitleOverlay bleiben unverändert. */
  renderMode?: 'og' | 'embedded';
  /**
   * Overlay-Stufe-B (2026-06-10): true bei `?chrome=minimal` — das
   * Portal-Overlay rendert Like/Save/Share selbst (same-origin,
   * authentifiziert). Unterdrückt SideActions + embedded-
   * OverflowActionsMenu; Inhalt-Chrome (TitleOverlay, CompanyLogo,
   * Credits) bleibt. Nur sinnvoll zusammen mit renderMode="embedded".
   */
  chromeMinimal?: boolean;
  /**
   * VOTE-/RATING-Abgabe melden. Setzt NUR ScrollyViewerPage bei echten
   * Lesern; die Vorschau-Route (/preview/s) setzt sie nie (27.09.2026).
   */
  onCtaAntwort?: (abgabe: CtaAbgabe) => void;
  /**
   * Handy-Ansicht erzwingen (Jan 15.09.2026, `?ansicht=handy` vom Portal):
   * Pro-Editor-Scrollys starten damit am Desktop im Handy-Rahmen mit
   * Vollbild-Umschalter statt immer im Vollbild. Ohne Parameter gilt die
   * Regel vom 11.09. unveraendert.
   */
  handyAnsicht?: boolean;
  /**
   * Seiten-Scroll (Benedikt 28.09.2026): am Handy scrollt die ganze Seite
   * statt des Reader-Kastens, damit Safari die Browser-Leiste einklappt.
   * Standard je Umgebung (dev an), pro Aufruf `?seitenscroll=0|1`.
   */
  seitenScrollTest?: boolean;
  /**
   * Eingebettet in ein iframe (Server: Sec-Fetch-Dest). Dann kein Seiten-
   * Scroll, s. seitenScrollNutzbar (Befund 29.09.2026, Hero auf welcome).
   */
  imIframe?: boolean;
  // Hinweis: Das frühere `appEmbedded`-Prop (`?open`) wurde 2026-08-13 entfernt.
  // Die App/Browser-Unterscheidung läuft jetzt client-seitig über
  // isInAppWebView(navigator.userAgent) — `?open` kam in echtem WK-App-Traffic
  // nie an, und ein Website-Container mit `?open` in einem echten Browser soll
  // das Schliessen-X behalten (→ Org-Homepage). Siehe lib/in-app-webview.ts.
  /**
   * Plan-1.9 Phase 3.B.CP2 — Open-Graph-Anchor 1-indexed Scene-Index aus
   * URL (`#scene-N` oder `?at=scene-N`). Vike-Page parst via
   * `parseSceneAnchor` und reicht das Resultat hierher durch.
   *
   * Konflikt-Logik mit Sub-Phase 3.A Resume-State (Bene-Entscheidung
   * 2026-05-11, siehe docs/PHASE-3-PRE-VERIFIKATION-2026-05-10.md
   * Sub-Phase-3.B-Sektion):
   *
   *   1. Hash gewinnt über Resume — Sharer-Intent ist intentionaler als
   *      impliziter Resume-State.
   *   2. Resume-Toast wird unterdrückt wenn Hash-Anchor da ist (würde
   *      User verwirren — er klickt einen Tweet-Link und sieht
   *      "Weiterlesen ab Slide X"-Toast statt der erwarteten Scene).
   *   3. localStorage-Eintrag bleibt erhalten (kein clearResumeState),
   *      damit der nächste Visit ohne Hash wieder das Resume-Pattern
   *      sehen kann.
   *   4. useResumeState-Hook bleibt aktiv (save-Path persistiert weiter
   *      die Scroll-Position), damit der Hash-Visit selbst zu einem
   *      neuen Resume-State führt.
   */
  initialSceneIdxOneIndexed?: number;
}) {
  const isOg = renderMode === 'og';
  const isEmbedded = renderMode === 'embedded';
  // Org-Homepage als Ziel des Schliessen-X bei geteilten/Direkt-Links (statt
  // der Scrolly-Plattform). Org-konfigurierbar via legalLinks.websiteUrl; leer
  // oder ungueltig → bisheriges Verhalten (PLATFORM_ORIGIN). Siehe handleClose.
  const orgHomepageUrl = safeOrgHomepage(scrolly.legalLinks?.websiteUrl);
  // In-App-WebView (native App-Container) vs. echter Browser. Das Schliessen-X
  // (und der Titel-Overlay) werden NUR in der nativen App unterdrueckt — dort
  // schliesst die App selbst (sonst „zwei Kreuzchen"). In JEDEM echten Browser
  // (geteilter Link, Website-Container) bleibt das X sichtbar → Org-Homepage.
  // Ersetzt das alte `?open`-Gate (appEmbedded), das in echtem WK-App-Traffic
  // nie ankam (die App laedt ohne `?open`); die WK-App-WKWebView ist stattdessen
  // am fehlenden Safari-Token erkennbar (s. lib/in-app-webview.ts).
  //
  // Bevorzugt die SSR-seitig aus dem Request-UA bestimmte Prop (server+client
  // identisch → kein X-Blitz nach der Hydration). Fallback navigator.userAgent
  // nur, wenn die Prop fehlt (Mock-/Alt-Pfade).
  const inAppWebView =
    inAppWebViewProp ??
    isInAppWebView(
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    );
  const { loggedIn, signIn } = usePublicAuth();
  const [signInOpen, setSignInOpen] = useState(false);

  // Mode toggle (Phone ↔ Immersive). Kontrollierter Modus via Prop, sonst
  // Fallback auf lokalen State. Vorlage Z. 1473-1477: In-Overlay-Swap,
  // kein Browser-Fullscreen. Bene-Q1: PhoneFrame-Größe wie
  // portal.scrolly.com (responsive 3:4 — aber Vorlage-Look mit
  // Notch/Glow bleibt aus HTML-File).
  const [localMode, setLocalMode] = useState<ViewerMode>('phone');
  const mode = modeProp ?? localMode;
  const onToggleMode =
    onToggleModeProp ??
    (() => setLocalMode((m) => (m === 'phone' ? 'immersive' : 'phone')));

  // Plan-N (2026-05-22): Renderer-Progress kommt jetzt von packages/-
  // ScrollyViewer.onProgress (0..1). Wir multiplizieren zu 0..100 für
  // ProgressHairline.
  const [progress, setProgress] = useState(0);

  // iOS-Audio-Permission-Anchor (2026-05-28, re-implementation der
  // ursprünglichen Stage-Gating-Mechanik aus Commit 8daeaba). Vor Plan-N
  // Radikalkur (5fd3d6c) mountete der Renderer erst NACH User-Click.
  // Im Plan-N Refaktor ging das verloren — ScrollyViewer mountet sofort,
  // Renderer initialisiert sich vor User-Activation, erste Video.play()
  // läuft pre-Click → iOS Safari blockt für immer.
  //
  // Fix: chromeHasStarted-State; ScrollyViewer wird erst gemountet wenn
  // chromeHasStarted=true. Pre-Click rendern wir CoverSection direkt in
  // ViewerChrome (ohne Renderer-Mount), Click setzt chromeHasStarted=true,
  // ScrollyViewer mountet im User-Activation-Context, Video.play() läuft.
  //
  // OG-Mode-Auto-Start: Headless-Browser muss Renderer ohne Click
  // mounten, sonst sind alle OG-Image-Screenshots leer.
  const [chromeHasStarted, setChromeHasStarted] = useState(false);
  // Seiten-Scroll-Test: Stand von useBodyScroll fuer Effekte, die vor dessen
  // Berechnung (weiter unten) deklariert sind.
  const useBodyScrollRef = useRef(false);
  /**
   * Boot-Deckel gegen den weissen Blitz (17.08.2026, Lauras Launch-Blocker).
   *
   * Beim Klick wechselt der Baum von Pfad (1) auf Pfad (2): der Vor-Klick-
   * Vorhang wird ABGEBAUT und ScrollyViewer samt Leinwand neu aufgebaut. Im
   * DOM-Protokoll steht beides in derselben Millisekunde:
   *     130 ms  −  <section[cover-section]>
   *     130 ms  +  <canvas>
   * Die Leinwand ist in dem Moment leer. Uebrig bleibt die weisse Flaeche mit
   * Stern und Ladekringel — in Benedikts 60-Bilder-Aufnahme sechs Bilder lang
   * (Helligkeit 164 → 193 → 164, 100 ms).
   *
   * Der Deckel haelt fuer diese Spanne ein Standbild desselben Vorhangs ueber
   * der Buehne. Er verschwindet, sobald die Leinwand da ist UND zwei Bilder
   * vergangen sind — also genau dann, wenn wirklich etwas zu sehen ist.
   *
   * Warum nicht `onViewerReady`: das feuert erst, wenn ALLE Ebenen geladen
   * sind (bei Lauras Scrolly ~1,5 s nach dem Klick). So lange wuerde der
   * Deckel den Intro-Lauf verdecken.
   */
  // Der Boot-Deckel (Zwischen-Overlay bis zum ersten Canvas-Frame) ist
  // ERSATZLOS raus (24.08.2026): das Vorschalt-Cover bleibt jetzt selbst als
  // dieselbe React-Instanz montiert, bis alle Assets geladen sind — es gibt
  // keine Luecke mehr, die ein Deckel ueberbruecken muesste (und der
  // Instanzwechsel war als Ruck/zweiter Titelscreen sichtbar).
  useEffect(() => {
    if (isOg && !chromeHasStarted) setChromeHasStarted(true);
  }, [isOg, chromeHasStarted]);

  // Der frühere Start-DECKEL (EIN-Cover-Choreografie, Jans Wunsch 08.08.2026)
  // wurde 2026-08-13 ENTFERNT. Er sollte nach START das Cover über dem bootenden
  // Player halten, lag aber mit z=45 hinter Canvas (998) UND dem ScrollyViewer-
  // eigenen renderCover (CoverSection z=1002) — ein späterer Cover-z-Index-Deploy
  // hob den renderCover über ihn. Der Deckel deckte also nichts mehr ab, sein
  // Titel „peekte" nur unter dem renderCover hervor = doppelter Titel
  // (WESER-KURIER-Regression). Der renderCover deckt den Boot ohnehin sauber ab
  // (verifiziert: elementFromPoint Bildschirmmitte = Cover-Section) und zeigt
  // Titel + Ladeanzeige — er ist der einzige Cover. Kein Deckel-State mehr nötig.

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (!chromeHasStarted || progress === 0) return;
    setIsScrolling(true);
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 800);
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [progress, chromeHasStarted]);

  useEffect(() => {
    if (!chromeMinimal || typeof window === 'undefined') return;
    try {
      window.parent.postMessage({ type: 'scrolly:scrollState', isScrolling }, '*');
    } catch { /* cross-origin safety */ }
  }, [isScrolling, chromeMinimal]);

  // iOS Safari: after bfcache restore (e.g. back from Unsplash credit) layout
  // dvh and visualViewport diverge → dead strip at the bottom. Re-sync shell
  // height on pageshow only (not on every URL-bar toggle — that stays cached).
  const [liveViewportHeight, setLiveViewportHeight] = useState<number | null>(
    null,
  );
  const [viewportSyncGeneration, setViewportSyncGeneration] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => {
      setLiveViewportHeight(readLiveViewportHeight());
      setViewportSyncGeneration((g) => g + 1);
    };
    sync();
    window.addEventListener("pageshow", sync);
    // Fenster-Resize (Jans Fund 08.08.2026): Die Shell — Phone-Frame samt
    // SideActions-Rail — lief beim Verziehen des Desktop-Fensters nicht mit
    // und stand danach neben/über dem sichtbaren Bereich. Auf TOUCH-Geräten
    // weiterhin nur bei BREITEN-Wechsel neu messen: der iOS-URL-Leisten-
    // Kollaps ändert ausschließlich die Höhe und soll gecacht bleiben
    // (s. Kommentar oben; dieselbe Regel wie der Per-Breite-Höhen-Cache im
    // VisualStoryStore). Orientierungswechsel ändert die Breite → synct.
    let letzteBreite = window.innerWidth;
    const onResize = () => {
      const istTouch = navigator.maxTouchPoints > 0;
      if (istTouch && window.innerWidth === letzteBreite) return;
      letzteBreite = window.innerWidth;
      sync();
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Pre-START-Scroll-Sperre (2026-06-03): Vor START ist ScrollyViewer NICHT
  // gemountet → der reguläre Scroll-Block (useBlockVertScroll lockt scrollRef)
  // ist inaktiv. Der Body (global.css: min-height:100vh) ist auf iOS höher als
  // der sichtbare Viewport → nativ scrollbar → Cover + Pinned-Overlays (Scrolly-
  // Stern) driften beim Wischen hoch, während das fixed-Chrome korrekt bleibt.
  // Lock html+body-overflow solange das Cover steht; Restore bei START/Unmount.
  // Nicht im OG-Mode (statischer Screenshot, kein Scroll).
  useEffect(() => {
    if (isOg || chromeHasStarted || typeof document === 'undefined') return;
    // Seiten-Scroll-Test: hier sperrt useBlockVertScroll das html-Element bis
    // START. Diese Sperre wuerde beim Aufraeumen dessen "hidden" als Vorwert
    // zuruecksetzen und die Seite nach START wieder festnageln.
    if (useBodyScrollRef.current) return;
    const html = document.documentElement;
    const { body } = document;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, [isOg, chromeHasStarted]);

  // Resume-State — Plan-N Bene-Feedback (2026-05-22 22:30): Auto-Resume
  // DEAKTIVIERT. Cover muss IMMER zuerst sichtbar sein, sonst überspringt
  // der Auto-Resume das Cover (Bene-Befund: "Kein Start Button kein
  // Startfeld").
  //
  // localStorage-Save bleibt aktiv (für Plan-2-UX "Continue Reading"-
  // Pattern wenn klar ist wie das aussehen soll). Hash-Anchor (Open-
  // Graph) bleibt funktional — der ist explicit User-Intent.
  const resumePublicKey = scrolly.publicKey ?? '';
  const currentScrollyVersion = scrolly.updatedAt ?? '';
  const { save: saveResumeState } = useResumeState(resumePublicKey);

  // Auto-Resume DEAKTIVIERT — Cover-First-Pattern. localStorage-Read
  // entfernt um sicherzustellen dass Cover IMMER sichtbar ist beim
  // ersten Render.
  const autoScrollPercent: number | undefined = undefined;

  const handleScrollProgress = (percent: number) => {
    if (!resumePublicKey) return;
    saveResumeState(percent, currentScrollyVersion);
    // Analytics-Quelle: useViewTracking (25-%-View) und useDurationTracking
    // (maxProgress → Scroll-Through) lesen diesen Stand statt window.scrollY,
    // das im Container-Scroll immer 0 ist (s. lib/reader-progress.ts).
    publishReaderProgress(resumePublicKey, percent);
  };

  // Beim Wechsel/Unmount den gemeldeten Stand verwerfen, damit ein erneuter
  // Besuch desselben Scrollys in dieser Tab-Session nicht mit dem alten
  // Fortschritt startet.
  useEffect(() => {
    if (!resumePublicKey) return;
    return () => clearReaderProgress(resumePublicKey);
  }, [resumePublicKey]);

  // Keyboard-Shortcuts: Esc → Close, m → Mode-Toggle. Pfeil links/rechts
  // schalten seit 30.08.2026 die Slides — das macht die NavButtonsSpalte im
  // Player selbst (überall, auch Previews). Der alte Progress-Stub hier
  // (stepProgress ±10) fütterte nur den CreditsOverlay-Stand an der echten
  // onProgress-Quelle vorbei — entfernt, sonst zeigte ein Tastendruck den
  // Bildnachweis einer anderen Slide.
  useViewerKeyboard({
    onEscape: () => handleClose(orgHomepageUrl),
    onModeToggle: onToggleMode,
  });

  // Mobile-Detection — Plan A B.4 konsolidiert via packages/viewer.
  // isCompact (max-width:768px || tablet-portrait) → effectiveMode =
  // immersive. Verhindert Phone-im-Phone auf iPhone.
  // Plan-N2 Block 3 v3: initialIsMobile aus pageContext.viewportHint
  // (UA-Sniffing) als Initial-State — kein Hydration-Flash.
  const { isCompact } = useResponsiveViewer(
    initialIsMobile,
    true, // hydrationSafe: public/-Vike-SSR — Client-First == SSR (Fix #418)
  );
  // Embedded-iframe (Portal-Modal): Der viewportHint kommt aus der UA — im
  // Portal-iframe auf dem Desktop ist die UA=Desktop, der ECHTE iframe-Viewport
  // ist aber die schmale Hochkant-Box (3:4). SSR rendert dann den Desktop-Frame,
  // der Client aber compact/immersive → React-Hydration-Mismatch (#418): das
  // Cover-Overlay (Titel+START) bleibt über der durchscrollbaren Story hängen.
  // Die Embedded-Box IST immer die schmale Phone-Box → isMobileViewport fest
  // auf true, damit SSR==Client (kein #418) und kein Phone-im-Phone im ohnehin
  // gerahmten iframe. Direktaufruf (UA=Desktop, Viewport=Desktop) unverändert.
  const isMobileViewport = isEmbedded ? true : isCompact;
  // PRO-EDITOR-SCROLLYS: nie Phone-Frame, auf keinem Geraet (Jan + Benedikt,
  // 11.09.2026). Erkennung am Dokument, nicht am Link: Template-Editor-
  // Dokumente tragen eine templateStructure, Pro-Editor-Dokumente nicht
  // (dieselbe Regel wie beim Branding im Reader). Ohne postJson (Mock-
  // Karten) bleibt es beim Template-Verhalten. SSR und Client rechnen aus
  // denselben Daten — kein Hydration-Sprung.
  // Aus dem Portal (`?ansicht=handy`, Jan 15.09.2026) gilt die Pro-Editor-
  // Ausnahme nicht: Rahmen + Umschalter wie bei Template-Scrollys.
  //
  // NEUE REGEL 28.09.2026 (Benedikt + Jan): Am Desktop starten ALLE Scrollys
  // im Handy-Rahmen mit Umschalter, auch Pro-Editor-Scrollys — die Regel vom
  // 11.09. ist aufgehoben. Sofort und NUR Vollbild gibt es nur noch, wenn der
  // Ersteller „Nur Vollbild" gewählt hat (Story-URL-Kasten, gespeichert am
  // Scrolly — der Link bleibt ohne Zusatz). Aus dem Portal (`?ansicht=handy`)
  // gilt auch das nicht: dort immer Rahmen + Umschalter.
  const proEditorVollbild = scrolly.nurVollbild === true && !handyAnsicht;
  const effectiveMode: ViewerMode =
    isOg || isMobileViewport || proEditorVollbild ? 'immersive' : mode;
  // Umschalter Vollbild <-> Handy-Ansicht: nur am Desktop und nur, wenn das
  // Scrolly nicht ohnehin Vollbild laeuft. Nicht gemerkt: jeder Aufruf
  // startet wieder im Rahmen (Beschluss 11.09.2026).
  const zeigeModusUmschalter = !isOg && !isEmbedded && !isMobileViewport && !proEditorVollbild;

  // scrollRef = funktional rootRef wie app/-PublicSlugViewerPage:165.
  // Wird gleichzeitig viewerRootContainerRef + heightRef + blockScrollElementRef
  // für packages/-ScrollyViewer. Hängt jetzt direkt unter dem Mid-Wrapper
  // (Mobile) bzw. unter dem Phone-Frame (Desktop) — die alten Sub-Components
  // Stage / ScrollyContainer / ScrollyViewerHost sind gelöscht, das spart
  // ~3 Wrapper-Schichten zwischen viewport und Renderer.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const imgproxyUrl = import.meta.env.VITE_IMGPROXY_URL ?? '';

  const isPhone = effectiveMode === 'phone';
  const wide = !isPhone;

  // Karte skaliert (28.09.2026, Benedikt/Jan): Die Karte wird als
  // Handy mit hoechstens 600 px Layout-Breite gebaut (Slide-Layouts kippen
  // darueber) und dann per transform:scale auf die Fensterhoehe gezogen —
  // wie Instagram-Stories. Nur im Karten-Modus am Desktop.
  // Den Faktor liest der Renderer fuer Puffer, Bildbreite, Texturstufe und
  // Video-Fassung (packages/renderer/src/utils/kartenSkala.ts). Bewusst kein
  // Import aus @scrolly/renderer: der Index zoege three.js ins erste Bundle.
  const setzeKartenSkala = (k: number) => {
    (window as unknown as Record<string, number>).__scrollyKartenSkala = k;
  };
  const [kartenSkala, setKartenSkala] = useState(1);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const messen = () => {
      const karteAktiv =
        effectiveMode === 'phone' && !window.matchMedia(FULLSCREEN_QUERY).matches;
      let k = 1;
      if (karteAktiv) {
        const rand = Math.min(40, Math.max(20, window.innerHeight * 0.025));
        const sichtBreite = Math.min(
          window.innerWidth - 40,
          (window.innerHeight - 2 * rand) * KARTEN_SEITENVERHAELTNIS,
        );
        const layoutBreite = Math.min(sichtBreite, 600);
        k = layoutBreite > 0 ? Math.max(1, sichtBreite / layoutBreite) : 1;
      }
      setzeKartenSkala(k);
      setKartenSkala(k);
    };
    messen();
    window.addEventListener('resize', messen);
    return () => {
      window.removeEventListener('resize', messen);
      setzeKartenSkala(1);
    };
  }, [effectiveMode]);

  // Iteration 3 (Plan-2 iOS-Jump): body-scroll-auf-iOS verworfen — auf echtem
  // iPhone verursachte es einen iOS-nativen window.scrollY-Reset auf Slide 0
  // beim URL-Bar-Kollaps. Zurück zu container-scroll ÜBERALL. db137a99 hatte
  // body-scroll re-aktiviert → Regression (iPhone-Crash). Revert 2026-06-21.
  //
  // Seiten-Scroll-Test (Benedikt 28.09.2026, `?seitenscroll=1`): ein vierter
  // Anlauf, diesmal VOM ERSTEN BILD AN (kein Umschalten beim START wie im
  // Juni) und mit fester Hoehe: Buehne, Kamera und Slides messen 100lvh, der
  // Vorhang 100svh. Beides aendert sich nicht, wenn Safari die Leiste ein-
  // oder ausklappt, also gibt es nichts neu zu berechnen. Nur Handy-Vollbild,
  // nie eingebettet, nie OG. Aus = Container-Scroll wie bisher.
  // Befund 29.09.2026 (Hero auf welcome.scrolly.com zeigte nur den Stern):
  // Die Entscheidung muss von der ersten Zeichnung an feststehen, weil die
  // Scroll-Engine ihre Quelle nur beim Anlegen liest. Deshalb zaehlt der
  // UA-Hinweis des Servers (initialIsMobile), nicht der spaetere matchMedia-
  // Wert, und ein iframe (Sec-Fetch-Dest) schaltet den Seiten-Scroll ab.
  // Regeln und Begruendung: public/src/lib/seiten-scroll.ts.
  const useBodyScroll = seitenScrollNutzbar({
    seitenScrollTest,
    isOg,
    isEmbedded,
    imIframe,
    initialIsMobile,
    isMobileViewport,
    wide,
  });
  const scrollSource: 'container' | 'window' = useBodyScroll
    ? 'window'
    : 'container';
  useBodyScrollRef.current = useBodyScroll;
  // Scroll-Sperre bis START: im Seiten-Scroll sperrt/entsperrt der Player das
  // html-Element (die Seite), nicht den Reader-Kasten. Bliebe es beim Kasten,
  // setzte das Entsperren dort overflow-y:auto — der Kasten wuerde zum
  // Scroll-Container und die sticky Ebenen (CTA, Nav-Pfeile) klebten an ihm
  // statt am Bildschirm.
  const dokumentRef = useRef<HTMLElement | null>(null);
  if (typeof document !== 'undefined') dokumentRef.current = document.documentElement;

  // scroll-container Style = app/-rootRef-Pattern. containerType:size +
  // contain:content + containerName verankern die Höhenkette und isolieren
  // den Renderer-Subtree-Layout — verhindert iOS-Compositor-Memory-Spike
  // durch zu viele unnötige Layer.
  // Body-scroll (Plan-2): im window-Mode ist scrollRef KEIN Scroll-Container —
  // die Slides fließen ins Document, der Body scrollt (window.scrollY). scrollRef
  // = Layout-Anker (minHeight:100lvh reserviert das Cover) + Renderer-Container-
  // Context. containerType "inline-size" (NICHT "size") behält die @container
  // (max-width:850px)-Query, ohne Höhen-Containment (das würde height:auto auf 0
  // zwingen + body-scroll brechen). contain:content (paint) raus — würde klippen.
  const scrollContainerStyle: React.CSSProperties = {
    width: '100%',
    height: useBodyScroll ? 'auto' : '100%',
    minHeight: useBodyScroll ? '100lvh' : undefined,
    position: 'relative',
    overflowY: useBodyScroll ? 'visible' : 'hidden',
    // Seiten-Scroll: 'clip' statt 'hidden' — hidden auf EINER Achse macht die
    // andere zu 'auto' und den Kasten damit zum Scroll-Container.
    overflowX: useBodyScroll ? 'clip' : 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    contain: useBodyScroll ? undefined : 'content',
    // Body-scroll: KEIN containerType — dessen contain:layout würde scrollRef zu
    // einem Nicht-scrollenden Sticky-Scrollport machen → der sticky WebGL-Canvas
    // klebt am scrollRef statt am Viewport → scrollt weg. Im window-Mode misst der
    // Renderer ohnehin window.innerHeight (nicht die @container-Query); die
    // @container(max-width:850px)-Slide-Stylings werden ggf. via @media gespiegelt.
    // KEIN container-type mehr im Reader (Jans Safari-Fund 27.08.2026,
    // per ?probe-Schalter isoliert): Jedes Containment am Scroller ODER
    // seinen Huellen ('size' wie 'inline-size', auch auf der Maske)
    // bringt WebKits Compositor dazu, das Scroll-Maximum an der
    // Sticky-Grenze des Canvas festzunageln — Safari zog scrollTop ohne
    // JS-Schreiber dorthin zurueck (Hakeln am Scrolly-Ende, Footer
    // unerreichbar; mutmasslich auch die Wurzel des iPhone-Footer-
    // Ruecksprungs #484). Die fruehere @container(max-width)-Regel der
    // Slides ersetzt der Player selbst (.viewer-schmal via innerWidth).
    // Safari-Scroll-Anchoring aus (Jans Hakel-Fund am Scrolly-Ende,
    // ?scrolldebug 27.08.2026): Die Slide-Text-Overlays bewegen sich
    // layoutmaessig mit dem Scrollfortschritt; waehlt Safari so ein
    // Element als Anker, "korrigiert" es scrollTop zurueck →
    // Rueckkopplung mit Fixpunkt (Spruenge auf exakt 10734, ohne
    // JS-Schreiber). Alle app/-Previews tragen den Opt-out laengst
    // (TemplateHoverPreview, ScrollyPreviewStageModal, ...) — nur der
    // Reader hatte ihn nie.
    overflowAnchor: 'none',
    // BLITZKANTE Teil 3 (Jans Chrome+Safari-Nahaufnahmen 27.08.2026): Der
    // Szenen-Inhalt ist durch die aspectRatio-Klammer einen Bruchpixel
    // kleiner als dieser Container — an der Ecken-RUNDUNG wächst der Spalt
    // zwischen gerader Inhaltskante und gebogener Maskenkante über 1px
    // (Deckring reicht nicht) und liegt ÜBER dem Masken-Hintergrund.
    // Container darum auch im Phone-Rahmen schwarz — wie der echte
    // Mobile-Pfad; jeder Restspalt wird bezel-schwarz statt weiß.
    background: '#000',
  };

  // Renderer-Inhalt. Drei Pfade:
  //
  // (1) Pre-Mount-Cover (scrolly.postJson da, !chromeHasStarted): CoverSection
  //     direkt — ScrollyViewer NICHT gemountet, daher kein Renderer, kein
  //     Video.play() vor User-Click. Click auf Start-Button setzt
  //     chromeHasStarted=true. iOS-Audio-Permission-Anchor.
  //
  // (2) Renderer-Mount (chromeHasStarted=true ODER isOg): ScrollyViewer mit
  //     autoStart={true} (überspringt internes Cover, schon im User-Activation-
  //     Context gemountet). renderCover liefert CoverSection wieder — damit
  //     Cover als position:absolute-Wrapper-Item INNERHALB scrollRef bleibt
  //     und User per Scroll-Up zurück navigieren kann (Bene-iPhone-Befund
  //     2026-05-28). autoStart triggert scrollToStartSlide → User landet
  //     direkt auf slide[0], Cover ist out-of-view aber scroll-erreichbar.
  //
  // (3) Mock-Fallback (kein scrolly.postJson): wie vorher, kein Renderer-Mount
  //     sinnvoll, nur Layout-Stub.
  // Hydration-sicher (analog shellHeight unten): beim ersten Render ist
  // liveViewportHeight null → SSR UND Client-First-Render MÜSSEN denselben
  // Wert liefern. window.innerHeight hier ergibt client-seitig z.B. 812 statt
  // SSR-800 → React-#418 (Hydration-Mismatch) am <section data-testid="cover-
  // section"> (Cover-Höhe). Der pageshow-useEffect (oben) setzt
  // liveViewportHeight direkt nach dem Mount auf den echten Wert → Cover
  // snappt sofort auf volle Höhe.
  const innerHeight = liveViewportHeight ?? 800;
  const shellHeight =
    liveViewportHeight != null ? `${liveViewportHeight}px` : '100dvh';
  // Custom-Fonts BEREITS für den Pre-Mount-Cover laden. Der Font-Loader lebte
  // bisher nur in ScrollyViewer (mountet erst nach „Start"), deshalb stand der
  // Cover-Titel vor dem Klick immer in der Default-Schrift statt in der
  // Org-Schrift (Weser-Kurier). Wir laden hier dieselben eingebackenen Fonts
  // (Fallback: authorFonts) und reichen die Familien an CoverSection durch.
  const slideOptionsForFonts = (
    scrolly.postJson?.data as
      | { data?: { presentationOptions?: { slideOptions?: unknown } } }
      | undefined
  )?.data?.presentationOptions?.slideOptions as
    | { fontInfos?: FontInfoDto[]; fontMapping?: FontMapping | null }
    | undefined;
  const bakedFontInfos = slideOptionsForFonts?.fontInfos;
  const bakedFontMapping = slideOptionsForFonts?.fontMapping;
  // Merge baked + authorFonts (same reason as ScrollyViewer): applyBranding
  // rewrites fontMapping to the org font names, but baked fontInfos may carry
  // stale, mismatched entries with broken URLs. authorFonts holds the canonical
  // ones. Loader matches by family, so unmatched entries are harmless.
  const coverFontInfos = [
    ...(bakedFontInfos ?? []),
    ...(scrolly.authorFonts?.fontInfos ?? []),
  ];
  const coverFontMapping =
    bakedFontMapping?.headline || bakedFontMapping?.text
      ? bakedFontMapping
      : (scrolly.authorFonts?.fontMapping ?? undefined);
  const coverFontFamilies = useCustomFontLoader(coverFontInfos, coverFontMapping);

  // Dachzeile: nur eine echte, im Scrolly gepflegte `coverTopline` zeigen —
  // KEINE generische „{Kategorie} · A Scrolly"-Platzhalterzeile mehr (von
  // WESER-KURIER als Dummy gemeldet; s. coverProps.eyebrowText unten).

  // Cover-Titel/-Vorhang aus den Scrolly-Metadaten (gespiegelt zur Editor-
  // Vorschau). Ohne postJson (Feed-Mocks) → Defaults.
  const coverMeta = scrolly.postJson?.data?.metadata;
  // firstSlideImage/Pre-Mount-Preload sind mit der Original-Architektur
  // (Szene bootet sofort, Vorhang ohne Bild) entfallen — der Renderer lädt
  // seine Assets selbst und meldet den Fortschritt an den Aufladen-Knopf.
  // Vorladen waehrend des Covers: Der Renderer mountet erst nach „Start", bis
  // dahin laedt kein Bild. Wir stossen die (via /img optimierten) Bild-URLs
  // schon jetzt per <link rel="prefetch"> an, damit „Start" die Renderer-
  // Requests aus dem Browser-Cache bedient statt neu zu laden. crossOrigin muss
  // "anonymous" sein: der Renderer laedt Bilder mit crossOrigin=anonymous
  // (THREE.ImageLoader) — nur ein gleich-attributierter Cache-Eintrag trifft.

  // Startbildschirm-Schalter (Jans Umbau 31.08.2026, Template-Editor):
  // metadata.hideCoverText = true heißt: keine Titeltexte, kein Vorhang —
  // NUR der START-Knopf (der bleibt immer: die User-Geste ist Pflicht für
  // iOS-Ton/Autoplay). Gespiegelt zur Editor-Vorschau (lib/cover-props).
  const startbildAus =
    (coverMeta as { hideCoverText?: boolean } | undefined)?.hideCoverText === true;
  const coverProps = {
    // coverTitle: echte Überschrift OHNE Dateinamen-Rückfall — leer zeigt
    // das Cover nur den START-Knopf (Jans Regel 30.08.2026). scrolly.title
    // (mit Rückfall) bleibt für Teilen/Chrome-Texte in Gebrauch.
    title: startbildAus ? '' : (scrolly.coverTitle ?? scrolly.title),
    // #6-Fix (Laura, 2026-07-28): der public-Cover zeigt den Untertitel
    // (metadata.subtitle), Rückfall auf description für Altdaten. Vorher wurde
    // hier die lange OG-Beschreibung gerendert. Der app/-Editor-Preview nutzt
    // ViewerChrome NICHT (eigener Pfad) — dort bleibt subtitle korrekt.
    description: startbildAus ? undefined : scrolly.subtitle || scrolly.description,
    // Nur die echte Dachzeile aus dem Scrolly; kein generischer Platzhalter.
    eyebrowText: startbildAus ? undefined : coverMeta?.coverTopline || undefined,
    // Org-Schrift schon vor dem Start (s.o.) — CoverSection nutzt diese
    // Familien mit Instrument-Serif/DM-Sans als Fallback.
    headlineFont: coverFontFamilies?.headlineFontFamily,
    textFont: coverFontFamilies?.textFontFamily,
    startButtonText: coverMeta?.startButtonText || undefined,
    // Bild statt Knopf (Pro-Editor-Startbildschirm, in die Metadaten
    // gespiegelt; Rueckfall auf die Start-Slide fuer Scrollys, die vor der
    // Spiegelung gespeichert wurden) — s. @scrolly/post-types/start-button.
    startButtonImage:
      resolveStartButtonImage(coverMeta, scrolly.postJson?.data?.data?.slides) || undefined,
    // Knopf-GESTALTUNG aus dem Pro-Editor (Radius/Füllung/Rand/Akzent) —
    // gleiche Auflösung wie das Bild: Metadaten vor Start-Slide (Jans Fund
    // 24.08.2026: die Felder wurden nie gespiegelt, der Leser zeigte den
    // Standard-Knopf).
    startButtonStyle:
      resolveStartButtonStyle(coverMeta, scrolly.postJson?.data?.data?.slides) || undefined,
    // TYPOGRAFIE des Startbildschirms (Schrift + Größenfaktor für Titel-/
    // Unterzeile) — gleiche Auflösung: Metadaten vor Start-Slide (#459-Lücke,
    // gebaut 26.08.2026). Schlägt in CoverSection die Branding-Schrift.
    startTypography:
      resolveStartTypography(coverMeta, scrolly.postJson?.data?.data?.slides) || undefined,
    // W2 — Reihenfolge ist Absicht: Was im Scrolly steht, gewinnt IMMER.
    // Der Org-Wert fuellt nur die Luecke; ist auch er null, greift der
    // bisherige Default in CoverSection. Damit aendert sich fuer bestehende
    // Scrollys nichts, solange die Org nichts gewaehlt hat.
    position: coverMeta?.coverTextPosition ?? orgCoverTextPosition ?? undefined,
    showCurtain: !startbildAus && coverMeta?.hideIntroOverlay !== true,
    curtainBlur: coverMeta?.coverCurtainBlur,
    curtainTheme: coverMeta?.coverCurtainTheme,
    lang: LANG,
  };

  // Like/Save/Share am Scrolly-ENDE (Jans Ansage 24.08.2026) — ersetzt die
  // Floating-Rail unten rechts. Einmal komponiert, an beide AfterContent-
  // Render-Pfade (Mock-Fallback + renderFooter) durchgereicht.
  // Welche Knoepfe die Org zulaesst (showEngagement / showLikeSave), fuer
  // Band UND Mobil-Menue aus derselben Regel.
  const engagementSichtbar = engagementVisibility(scrolly);
  const engagementEnde =
    !isOg && !chromeMinimal && engagementSichtbar.any ? (
      <EngagementEnde
        scrolly={scrolly}
        loggedIn={loggedIn}
        onRequireSignIn={() => setSignInOpen(true)}
        publicKey={publicKey}
        engagement={engagement}
      />
    ) : null;

  const viewerContent = !scrolly.postJson ? (
    // (3) Mock-Fallback ohne Backend-postJson (Feed-Mocks, /__dev__/-Pfade,
    // NotFound-Retry). Nur sichtbar wenn das DB-Scrolly keinen postJson liefert.
    <>
      <CoverSection
        {...coverProps}
        coverImage={scrolly.coverImage}
        onStart={() => {}}
        progress={1}
        isOgMode={isOg}
        height={innerHeight}
      />
      <PlaceholderContent scrolly={scrolly} />
      <AfterContent scrolly={scrolly} wide={wide} engagementRow={engagementEnde} />
    </>
  ) : (
    // ORIGINAL-ARCHITEKTUR wiederhergestellt (Jans Klarstellung 24.08.2026,
    // ersetzt das Stage-Gating vom 2026-05-28): Die Szene bootet SOFORT im
    // Hintergrund — der ScrollyViewer mountet ohne Klick, Videos laden
    // (pausiert), und über der bootenden Slide liegt NUR der Vorhang
    // (CoverSection OHNE Bild). Benedikts Aufladen-Knopf (animateLoading)
    // lädt mit dem ECHTEN Boot-Fortschritt auf und rastet ein, wenn alles
    // geladen ist; der Klick (echte User-Geste → iOS-Ton ok) startet
    // Wiedergabe + Intro-Tween über controls.onStart. Es gibt nur noch
    // EINE Cover-Instanz — kein Vorschalt-Cover, kein Deckel, kein Foto
    // („Wir haben beim Titel immer nur den Vorhang").
    <ScrollyViewer
      id={scrolly.postJson.data.id ?? scrolly.id}
      post={scrolly.postJson}
      lang={LANG}
      viewerRootContainerRef={scrollRef}
      heightRef={scrollRef}
      blockScrollElementRef={useBodyScroll ? dokumentRef : scrollRef}
      scrollSource={scrollSource}
      viewportHeight={liveViewportHeight}
      viewportSyncGeneration={viewportSyncGeneration}
      authorFonts={scrolly.authorFonts}
      imgproxyUrl={imgproxyUrl}
      isOgMode={isOg}
      disableCompanyLogo={true}
      runningCtaImRahmen={true}
      onCtaAntwort={isOg ? undefined : onCtaAntwort}
      onProgress={(p) => setProgress(p * 100)}
      initialScrollPercent={autoScrollPercent}
      onScrollProgress={handleScrollProgress}
      initialSceneIdxOneIndexed={initialSceneIdxOneIndexed}
      renderCover={(controls) => (
        <CoverSection
          {...coverProps}
          // BOOT-BLENDE (Jans Auftrag 02.09.2026, Benedikts „Deckel bleibt
          // zu lange schwarz"): Das Cover-Webp deckt NUR die Boot-Phase —
          // es ist in <1 s da, waehrend der Canvas je nach Netz/CPU 2-14 s
          // braucht. Bei progress>=1 blendet CoverSection es weich aus und
          // die Regel vom 24.08. („nur der Vorhang ueber der Live-Slide")
          // gilt wieder unveraendert.
          bootImage={scrolly.bootBackgroundImage}
          headlineFont={controls.fontFamilies?.headlineFontFamily}
          textFont={controls.fontFamilies?.textFontFamily}
          onStart={() => {
            setChromeHasStarted(true);
            controls.onStart();
          }}
          progress={controls.progress}
          animateLoading
          isOgMode={controls.isOgMode}
          height={controls.height}
        />
      )}
      renderFooter={<AfterContent scrolly={scrolly} wide={wide} bodyFontFamily={coverFontFamilies?.textFontFamily} engagementRow={engagementEnde} />}
    />
  );

  // chromeOverlays (CompanyLogo + ScrollIndicatorEcho) sind Siblings von
  // scroll-container, innerhalb der scrollRef-Box.
  // Pinned-Overlays die nicht mitscrollen.
  // KEIN CreditsOverlay mehr (Jans WK-Fund 31.08.2026, „zwei Fotocredits,
  // einer haengt mit im Vorhang"): Den Foto-Nachweis rendert der Player
  // selbst (fixed unten, z-1200, mit Slide-Blende und titleCredits-Regel)
  // — das alte public-Overlay (D4-Port) zeigte denselben Text ein zweites
  // Mal und fuhr als absolute-Kind des Chrome-Containers mit dem Vorhang
  // hoch.
  // Body-scroll (Plan-2): die Overlays sind position:absolute relativ zur
  // scrollRef-Box. Im window-Mode scrollt der Body → ohne Eingriff würden sie
  // mitscrollen. Wrap in einen position:fixed inset:0 Container (pointerEvents:
  // none, damit Klicks/Scroll durchgehen) → Overlays bleiben viewport-fest.
  const chromeOverlaysRaw = !isOg ? (
    <>
      <CompanyLogo post={scrolly.postJson} />
      <ScrollIndicatorEcho
        scrollContainerRef={scrollRef}
        scrollSource={scrollSource}
      />
    </>
  ) : null;
  const chromeOverlays =
    chromeOverlaysRaw && useBodyScroll ? (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          // 9999, nicht 40 (Fund 29.09.2026, Prod): position:fixed bildet
          // einen eigenen Stapel — alles darin (Logo z-2000, Scroll-Pille
          // z-9999) kam nie ueber 40 hinaus und lag UNTER der fixen Buehne
          // (z-998) und den Textboxen (z-1000): Org-Logo unsichtbar, Pille
          // unsichtbar und nicht greifbar. 9999 = dieselbe Rangfolge wie im
          // Container-Modus, weiter unter Anmelde-Dialog (Z.modal 10000).
          zIndex: 9999,
        }}
      >
        {chromeOverlaysRaw}
      </div>
    ) : (
      chromeOverlaysRaw
    );

  // (Start-Deckel entfernt 2026-08-13 — s. Kommentar oben beim ex-deckerZustand.)

  return (
    <>
      <div
        className={
          (effectiveMode === 'immersive' ? 'scrolly-shell scrolly-shell--vollbild' : 'scrolly-shell') +
          (useBodyScroll ? ' scrolly-shell--seitenscroll' : '')
        }
        style={{
          // Plan-N2 Phase 2 Schritt 1 (2026-05-25 Bene): Outer-Container
          // auf app/-Pattern umgestellt. Vorher position:fixed inset:0
          // (Overlay-Pattern aus Vorlage) — das blockte den Body-Scroll und
          // brach die Höhenkette für scrollRef.clientHeight → timer stieg
          // beim Scrollen nicht → alle Layer "in lifetime" → alle Videos
          // spielten gleichzeitig. Plus: Safari iOS URL-Bar Auto-Hide
          // funktionierte nicht weil Safari kein Body-Scroll sah.
          //
          // app/-Pattern (app/src/pages/PublicSlugViewerPage.tsx:163,174):
          //   <div width:100vw height:100dvh overflow:hidden background:#000>
          //
          // 2026-05-28: 100lvh-Quick-Test (d221275) reverted. Schiebte
          // CoverSection-Bottom (Start-Button) unter iOS-Safari-URL-Bar
          // → Button nicht klickbar. URL-Bar-Auto-Hide-Strategie wandert
          // in Plan-2 (Body-Scroll-Architektur, siehe Memory
          // url_bar_auto_hide_plan_2).
          position: 'relative',
          width: '100vw',
          // Body-scroll (Plan-2): im window-Mode darf der Body scrollen — KEIN
          // height:100dvh + overflow:hidden (klippt + verhindert body-scroll).
          // minHeight:100dvh hält den Viewer mindestens viewport-hoch.
          height: useBodyScroll ? 'auto' : shellHeight,
          minHeight: useBodyScroll ? '100svh' : shellHeight,
          overflow: useBodyScroll ? 'visible' : 'hidden',
          // Plan-N3 Stufe A (Bene 2026-05-27): contain:content macht den
          // Viewer-Subtree zum Layout-Isolation-Punkt. Verhindert dass eine
          // Resize-Notification (z.B. iOS URL-Bar-Movement) den gesamten
          // Page-DOM neu rechnet. app/-PublicSlugViewerPage hat dieses
          // Property im mid-Wrapper (Z. 164), public/ hatte es gar nicht.
          contain: useBodyScroll ? undefined : 'content',
          background: effectiveMode === 'immersive' ? '#000' : '#111',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: BRAND_VAR.uiFont,
        }}
      >
        <style>{`@keyframes stageFadeIn { from { opacity: 0 } to { opacity: 1 } }
        `}</style>

        {/* Page-Level Chrome (Vorlage Z. 1431-1471 + 1479-1489) */}
        {/* Plan-N2 Block 1.b (2026-05-25 Bene): ProgressHairline komplett raus.
         * Verbatim: "rechten Scrollbalken viel zu breit". Auch bei letztem
         * Iteration: "kann ich nichts anfangen". Scroll-Progress ist
         * implicit über die Slides selbst sichtbar. */}

        {/* TopBar (X) / non-embedded OverflowActionsMenu (⋯) / SignInModal sind
         * ans Return-Ende gehoben — AUSSERHALB des contain:content-Outer-Div,
         * sonst klebt ihr position:fixed am Outer-Div statt am Viewport und
         * driftet beim Scrollen (Chrome-Scroll-Fix 2026-06-03). */}

        {/* Stage-Area — Plan-N3 Refaktor (Bene 2026-05-27):
         * Stage / ScrollyContainer / ScrollyViewerHost als Sub-Components
         * gelöscht. Hier inline-gerendert. Mobile = flat app/-3-tier-Pattern
         * (mid + scrollRef + ScrollyViewer). Desktop-Phone = Letterbox-
         * Decoration zusätzlich, scrollRef hängt innerhalb des Frames.
         * Beide Varianten teilen viewerContent + chromeOverlays + scroll-
         * containerStyle (definiert vor return). */}
        {/* EIN Shell-Markup fuer beide Modi (Jans "richtig machen" 02.09.2026):
            Frueher entschied JavaScript (isPhone) zwischen Phone-Frame- und
            Vollbild-Markup — der allererste Server-Paint konnte damit im
            falschen Gehaeuse stehen (Emulator/Tablet: Desktop-UA + schmaler
            Viewport) und sprang nach der Hydration um, samt Titelgroesse.
            Jetzt rendert IMMER das Frame-Markup, und DIESELBE Media-Query,
            die auch useResponsiveViewer nutzt (FULLSCREEN_QUERY), kollabiert
            es per CSS zur Vollbild-Variante — Geometrie steht ab dem ersten
            Byte, ohne JS. isPhone steuert nur noch Feature-Chrome. */}
        <style>{`
          /* COVER-HOEHE IN CSS (Jans letzter Sprung 02.09.: Titel rutschte
             104px, weil die Cover-Hoehe aus der JS-Messung kam — SSR 800px,
             gemessen z.B. 592px). Die Hoehe kommt jetzt aus DENSELBEN
             Formeln wie der Rahmen selbst und ueberstimmt den Inline-Wert:
             Frame-Modus = Kartenhoehe (KARTEN_BREITE * 4/3, seit 30.09.2026
             ohne Blende); Vollbild = 100dvh. Rahmen und Cover teilen sich
             KARTEN_BREITE, damit beide nie auseinanderlaufen. */
          .scrolly-phone-mask [data-testid="cover-section"] {
            height: calc(${KARTEN_BREITE} / ${KARTEN_SEITENVERHAELTNIS}) !important;
          }
          /* Vollbild auf ZWEI Wegen, dieselben Regeln (Jan + Benedikt 11.09.2026):
             1. per Media-Query (Handy, Tablet hochkant, Touch-Tablet quer) —
                DIESELBE Query wie useResponsiveViewer (FULLSCREEN_QUERY), ab
                dem ersten Byte ohne JS;
             2. per Klasse .scrolly-shell--vollbild, die JS setzt: Pro-Editor-
                Scrollys immer, Template-Scrollys nach dem Umschalter. */
          @media ${FULLSCREEN_QUERY} {
            ${vollbildRegeln('')}
          }
          ${vollbildRegeln('.scrolly-shell--vollbild ')}
          ${SEITENSCROLL_REGELN}
        `}</style>
        {(
          // Letterbox + 3:4-Karte ohne Handy-Blende (kollabiert per CSS).
          <div
            className="scrolly-shell-letterbox"
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
              position: 'relative',
              overflow: 'hidden',
              contain: 'content',
            }}
          >
            {/* jan-17 — Share-Hintergrund hinter dem Phone-Frame.
             * Der im Publishing-Editor konfigurierte Hintergrund
             * (solid/gradient + Blur/Opacity) wird via BrandingTokens →
             * CSS-Vars durchgereicht. Eigene absolut-positionierte Layer,
             * damit Blur/Opacity den Frame selbst NICHT beeinflussen
             * (mirror app/ PublicViewerPage). Fallback (kein Share-BG
             * gesetzt) = der bisherige Avatar-Radial-Gradient. */}
            {/* Heller Unterbau — NUR wenn ein Share-BG gesetzt ist (dann
             * emittiert tokensToCssVars --scrolly-brand-share-base). Sorgt
             * dafür, dass die Opacity über HELL komponiert (wie der Editor-
             * Preview, PublishingPage.tsx:445) statt über das dunkle #111.
             * Ohne Share-BG bleibt es transparent → Avatar-Fallback über #111. */}
            <div
              className="scrolly-shell-sharebg"
              style={{
                position: 'absolute',
                inset: 0,
                background: 'var(--scrolly-brand-share-base, transparent)',
              }}
            />
            {/* Weichzeichnen per `filter` auf dem Verlauf selbst, NICHT per
             * backdrop-filter-Ebene darüber (Jans Fund 13.10.2026: der Rand
             * flackerte beim schnellen Scrollen in Chrome und Safari, mit
             * Blur 0 nicht). backdrop-filter liest den Hintergrund bei jedem
             * zusammengesetzten Bild neu ein; ein gefilterter statischer
             * Verlauf wird einmal gerastert. Der Überstand von 2×Blur hält
             * die ausgefransten Filterkanten aus dem sichtbaren Bereich
             * (die Letterbox schneidet per overflow ab). */}
            <div
              className="scrolly-shell-sharebg"
              style={{
                position: 'absolute',
                inset: 'calc(-2 * var(--scrolly-brand-share-blur, 0px))',
                background: `var(--scrolly-brand-share-background, radial-gradient(ellipse at center, ${rgbaFromHex(scrolly.creator.avatar || '#FF3366', 0.25)}, transparent 60%))`,
                opacity: 'var(--scrolly-brand-share-opacity, 1)',
                filter: 'blur(var(--scrolly-brand-share-blur, 0px))',
              }}
            />
            <div
              className="scrolly-shell-frame"
              style={{
                width: KARTEN_BREITE,
                aspectRatio: '3 / 4',
                transform: kartenSkala > 1 ? `scale(${kartenSkala})` : undefined,
                transformOrigin: 'center center',
                flexShrink: 0,
                // Format 3:4 ohne Handy-Silhouette (Benedikt 30.09.2026, Vorbild
                // Widget-Lightbox auf news.scrolly.com): keine 4px-Blende mehr,
                // nur Eckenradius und Schatten wie die Lightbox.
                borderRadius: KARTEN_RADIUS,
                background: '#000',
                boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                className="scrolly-phone-mask scrolly-shell-mask"
                style={{
                  // BLITZKANTE Wurzel (Jans HUD-Messung 27.08.2026, Safari):
                  // height:100% im aspect-ratio-Rahmen loest WebKit gegen die
                  // GESAMThoehe inkl. padding auf (Maske 675.5 = Rahmen 675.5
                  // statt 667.5) — die Maske ragte 4px unter den Rahmen und
                  // deckte die untere Blende ab. Absolut mit inset 4 statt
                  // width/height:100% — keine Prozent-Aufloesung, kein Bug.
                  // Seit 30.09.2026 ohne Blende: inset 0.
                  position: 'absolute',
                  inset: 0,
                  borderRadius: KARTEN_RADIUS,
                  // Radius fuer den Blur-Vorhang (CoverSection) — s. dort
                  ['--scrolly-vorhang-radius' as string]: `${KARTEN_RADIUS}px`,
                  overflow: 'hidden',
                  // BLITZKANTE Teil 2 (Benedikts Test-Fund 27.08.2026): der
                  // 1px-Innenring (#416) deckt die AA-Mischspalte — laesst die
                  // Bruchpixel-Rundung der aspectRatio-Karte aber einen Spalt
                  // GROESSER als 1px (v. a. unten, auf Nicht-Retina), schien
                  // dort das Seiten-Weiss durch. Rahmenfarbe als Masken-
                  // Hintergrund macht jeden Restspalt an jeder Kante
                  // bezel-schwarz — unabhaengig von Fensterbreite und dpr.
                  background: '#000',
                }}
              >
                <div
                  ref={scrollRef}
                  className="scrolly-scroll-container"
                  style={scrollContainerStyle}
                >
                  {viewerContent}
                </div>
                {chromeOverlays}
                {/* Kein Innenring mehr (30.09.2026): Der 1px-Ring in Rahmenfarbe
                 * (BLITZKANTEN-DECKEL, 24.08.) deckte die Mischkante zur schwarzen
                 * 4px-Blende. Ohne Blende zeichnete er selbst eine schwarze Linie
                 * um die Karte (gemessen Chromium + WebKit: Helligkeit 0 an der
                 * Kante, ohne Ring direkter Uebergang Bild → Hintergrund). */}
              </div>
            </div>
          </div>
        )}

        {/* Die frühere SideActions-Floating-Rail (unten rechts) ist WEG
         * (Jans Ansage 24.08.2026): Like/Save/Share stehen jetzt am ENDE
         * des Scrollys (EngagementEnde, gerendert in AfterContent) — wer
         * durchgescrollt hat, kann dort liken/merken/teilen. */}

        {/* Das eingebettete 3-Punkte-Menue unten rechts (Gefaellt mir /
         * Speichern / Teilen) ist WEG (Benedikt 01.10.2026): dieselben
         * Knoepfe stehen am Scrolly-Ende (EngagementEnde), auch im iframe. */}

        {/* Das frühere TitleOverlay (generische Dachzeile „STORY · SCROLLY" +
         * Titel, immersive & progress<1) wurde 2026-08-13 ENTFERNT. Es nahm an,
         * das Cover sei beim Anzeigen schon weggescrollt — bei langsamem/stuck
         * Load (z.B. „Lädt 0%") ist der renderCover-Cover aber noch sichtbar und
         * zeigt SELBST Titel + echte Dachzeile („FOTOREPORTAGE") + Ladeanzeige;
         * das Overlay legte sich mit der generischen pinken „STORY · SCROLLY"-
         * Zeile drüber = doppelter Titel (WESER-KURIER-Call-Befund, Haake-Beck).
         * Seit der Deckel weg ist, deckt der renderCover den Load ohnehin voll
         * ab → das Overlay ist redundant. */}

        {/* Plan-N2 (2026-05-25 Bene-Reduktion): KeyboardHint entfernt.
         * Verbatim-Quote: "Die Erklärung is unwichtig die können wir wieder
         * rausnehmen." Keyboard-Shortcuts laufen weiterhin via
         * useViewerKeyboard — nur die UI-Pille unten ist weg. */}

        {/* Diagnose-Overlay (nur sichtbar bei ?videodebug=1) */}
        <VideoDebugOverlay />
        {/* Diagnose-Overlay (nur sichtbar bei ?scrolldebug=1) — Footer-Rücksprung */}
        <ScrollSprungOverlay />
        {useBodyScroll && <SeitenScrollHud />}
      </div>

      {/* Chrome-Scroll-Fix (2026-06-03): TopBar (X) + SignInModal AUSSERHALB
       * des contain:content-Outer-Div als Fragment-Geschwister, damit ihr
       * position:fixed am echten Viewport klebt (Ahnenkette: BrandingProvider
       * →#root→body, kein contain/transform).
       *
       * Bene 2026-06-13: Das non-embedded Mobile-3-Punkte-Menü (⋯) ist hier
       * entfernt — Engagement zeigt auf Mobile jetzt die Einzel-Leiste
       * (SideActions, oben gerendert), wie das Platform-Overlay. Die EMBEDDED-
       * Variante des ⋯-Menüs (oben, im Outer-Div) bleibt für den iframe-Pfad. */}
      {!isOg && !isEmbedded && !inAppWebView && (
        <TopBar
          closeUrl={orgHomepageUrl}
          modus={zeigeModusUmschalter ? effectiveMode : undefined}
          onToggleMode={onToggleMode}
        />
      )}
      {!isOg && (
        <SignInModal
          open={signInOpen}
          onClose={() => setSignInOpen(false)}
          onConfirmSignIn={() => {
            signIn(window.location.href);
          }}
        />
      )}
    </>
  );
}

// (TitleOverlay entfernt 2026-08-13 — s. Kommentar an der ehemaligen Render-
// Stelle. Der renderCover-Cover zeigt den Titel während des Loads selbst.)

// Helper: rgba from hex (für radial-gradient bg in PhoneStage).
function rgbaFromHex(hex: string, alpha: number): string {
  const sanitized = hex.replace('#', '');
  if (sanitized.length !== 6 && sanitized.length !== 3) {
    return `rgba(255, 51, 102, ${alpha})`;
  }
  const expanded =
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((c) => c + c)
          .join('')
      : sanitized;
  const r = parseInt(expanded.substring(0, 2), 16);
  const g = parseInt(expanded.substring(2, 4), 16);
  const b = parseInt(expanded.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ---- KeyboardHint ------------------------------------------------------
// Dezenter Hinweis unten mittig. Zeigt die Shortcut-Liste für 3s,
// blendet dann aus. Mouse-Move (Hover-Intent) setzt den Timer zurück
// und blendet wieder ein. `prefers-reduced-motion` deaktiviert den
// Opacity-Fade — der Hint wechselt dann instantan zwischen sichtbar
// und unsichtbar.

// ---- Stage -------------------------------------------------------------
// Kapselt den Content-Bereich zwischen TopBar und Colophon. Je nach Mode:
//   - phone:     3-Spalten-Grid mit Letterbox zentriert, Rail rechts daneben
//   - immersive: Content füllt den Viewport (abzüglich TopBar-Höhe), Rail
//                floatet als Absolute-Overlay rechts vertikal zentriert.

// ============================================================================
// Bene-Regel (2026-06-13): X führt IMMER auf die Platform — außer der Viewer
// wurde von einer ECHTEN Vorgängerseite geöffnet, dann zurück dorthin. Die
// Platform-Origin ist im Viewer-Client ohnehin hart auf platform.scrolly.online
// verdrahtet (usePublicAuth:82, OidcAuthProvider:67, allowed-return-domains) —
// wir nutzen denselben Wert. Folge: der Beta-Viewer schickt X (wie der
// Beta-Login heute schon) auf die Prod-Platform — konsistent, kein Sonderfall.
// env-gesteuert (2026-07-14): auf .com muss X/„Platform" auf die .com-Plattform
// zeigen. Viewer-Build setzt VITE_PORTAL_BASE_URL pro Umgebung. Default .online
// bleibt → bricht die .online-Linie nicht.
const PLATFORM_ORIGIN =
  import.meta.env.VITE_PORTAL_BASE_URL || 'https://platform.scrolly.online';

// Portal-Herkunft so frueh wie moeglich merken — VOR dem stillen SSO-Sprung,
// nach dem der Referrer leer ist (s. lib/herkunft-portal.ts).
merkePortalHerkunft();

// Org-Homepage-Ziel des Schliessen-X normalisieren/absichern. Nimmt den roh in
// legalLinks.websiteUrl gepflegten Wert (Org-Admin/DB), erlaubt NUR http(s),
// ergaenzt fehlendes Protokoll (bare Domain → https://). Alles andere
// (javascript:, data:, Muell, leer) → undefined ⇒ Fallback auf die Plattform.
function safeOrgHomepage(raw?: string): string | undefined {
  if (!raw || typeof raw !== 'string') return undefined;
  const v = raw.trim();
  if (!v) return undefined;
  const withProto = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  try {
    const u = new URL(withProto);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return undefined;
    return u.href;
  } catch {
    return undefined;
  }
}

// Teilen/Kopieren-Ziel: die aktuelle URL, aber OHNE Embed-/App-Parameter.
// Wird ein Scrolly aus einem App-/Container-Kontext geteilt, traegt
// window.location.href sonst `?open` (bzw. `?embedded`) mit — der Empfaenger
// landet dann wieder im Embed-Modus (kein Schliessen-X, falscher Close-Pfad)
// statt auf einer sauberen, teilbaren Leseseite. Wir strippen die Kontext-
// Parameter, sodass ein geteilter Link IMMER die eigenstaendige Ansicht oeffnet
// (X sichtbar → Org-Homepage). 2026-08-13.
/**
 * Teilen: native Share-Sheet, wenn vorhanden (Mobil), sonst Link in die
 * Zwischenablage. Ein Weg fuer Band und Mobil-Menue.
 */
function shareScrolly(title?: string): void {
  const url = shareableCurrentUrl();
  if (typeof navigator === 'undefined') return;
  if (typeof navigator.share === 'function') {
    void navigator.share({ title: title ?? 'Scrolly', url }).catch(() => undefined);
    return;
  }
  if (navigator.clipboard) {
    navigator.clipboard.writeText(url).catch(() => undefined);
  }
}

function shareableCurrentUrl(): string {
  if (typeof window === 'undefined') return '';
  try {
    const u = new URL(window.location.href);
    for (const p of ['open', 'embedded', 'renderMode', 'chrome', 'ansicht', 'sso']) {
      u.searchParams.delete(p);
    }
    return u.toString();
  } catch {
    return window.location.href;
  }
}

// orgHomepageUrl (optional): bei geteilten/Direkt-Links (Fall 3) fuehrt das X
// dorthin statt auf die Plattform — so behaelt der Kunde den Traffic seiner
// geteilten Scrollys. Back-Navigation (Fall 1/2) und App-Embed (Fall 0) bleiben
// bewusst unveraendert (Bene-Entscheidung 2026-08-13).
function handleClose(orgHomepageUrl?: string) {
  if (typeof window === 'undefined') return;
  // (0) Native App-WebView (z.B. WESER-KURIER-App). Der native Host hat sein
  //     eigenes Schließen und erwartet, dass „X" NICHT zur Scrolly-Plattform
  //     navigiert (bisheriger Bug: Fallback (3) schickte die WebView auf
  //     PLATFORM_ORIGIN), sondern zum Slider zurückführt. history.back() bringt
  //     den WebView-Fall zum Slider; ohne Vorseite signalisiert postMessage dem
  //     Host das Schließen. Nie PLATFORM_ORIGIN/Org-Homepage im App-Kontext.
  //     Gate: WebView-Erkennung statt `?open` — real kam `?open` nie an, und ein
  //     Website-Container mit `?open` in einem ECHTEN Browser soll das X auf die
  //     Org-Homepage schicken (Fall 3), nicht hier landen. In der App ist unser
  //     X ohnehin ausgeblendet; dieser Zweig greift nur noch via Esc/Programm.
  try {
    if (isInAppWebView(navigator.userAgent)) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.parent?.postMessage({ type: 'scrolly:close' }, '*');
      }
      return;
    }
  } catch {
    /* UA-Zugriff kann in exotischen WebViews werfen → regulärer Pfad unten */
  }
  // (1) Aus dem Portal via window.open geöffnet (mit opener) → Tab schließen
  //     kehrt zur öffnenden Seite zurück.
  if (window.opener) {
    window.close();
    return;
  }
  // (2) Referrer-Härtung: nur „zurück", wenn es eine ECHTE Vorgängerseite in
  //     diesem Tab gab (document.referrer gesetzt UND History-Tiefe > 1).
  //     Direkt-Links (WhatsApp/Messenger, getippte URL) haben leeren Referrer —
  //     auch wenn manche In-App-Browser mit history.length=2 (leere Vorseite)
  //     starten. So landet ein geteilter Link verlässlich auf der Platform
  //     statt auf einer leeren Back-Seite (Bene-Befund 2026-06-13).
  let hasRealReferrer = false;
  try {
    hasRealReferrer =
      !!document.referrer && new URL(document.referrer).origin !== 'null';
  } catch {
    hasRealReferrer = false;
  }
  // (2a) Aus dem PORTAL gekommen, aber der Referrer zeigt nicht (mehr) dorthin:
  //      das ist der Besuch eines angemeldeten Nutzers nach dem stillen
  //      SSO-Sprung (Referrer leer oder Keycloak). Zurueck zur Scrolly-Home —
  //      nicht per history.back() (das liefe in den Keycloak-Redirect) und
  //      schon gar nicht auf die Org-Homepage (Jans Fund 18.09.2026).
  if (kamAusDemPortal() && !istPortalReferrer(document.referrer)) {
    window.location.href = PORTAL_HOME_URL;
    return;
  }
  if (hasRealReferrer && window.history.length > 1) {
    window.history.back();
    return;
  }
  // (3) Direkt-Link / keine Vorgängerseite (z.B. geteilter Link aus WhatsApp,
  //     getippte URL) → Org-Homepage, falls die Org eine gepflegt hat
  //     (legalLinks.websiteUrl), sonst die Plattform. So landet der Leser eines
  //     geteilten Org-Scrollys beim X auf der Kundenseite statt auf scrolly.com.
  window.location.href = orgHomepageUrl || PLATFORM_ORIGIN;
}

// ---- Shared styles ------------------------------------------------------
const iconButtonStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#fff',
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// ============================================================================
// TopBar — Plan-N2 Bene-Reduktion (2026-05-25): minimal, nur Close-X.
// Creator-Card, Folgen-Button, Share, Mode-Toggle, More — alle weg.
// Fokus auf Story-Content, keine UI-Unruhe.
// ============================================================================
/**
 * Format der Karte am Laptop/Desktop: 3:4 ohne Handy-Blende (Benedikt
 * 30.09.2026). Vorbild ist die Widget-Lightbox (widget/src/lightbox.ts), die
 * seit Maerz 3:4 zeigt; das Portal-Overlay nutzt dasselbe Format
 * (portal/src/components/viewer/ViewerOverlay.tsx). Vorher 9:16 mit 4px-Blende.
 */
const KARTEN_SEITENVERHAELTNIS = 0.75;
const KARTEN_RADIUS = 16;

/**
 * Groesse der Karte am Laptop/Desktop (Benedikt + Jan 28.09.2026, Vorbild
 * Instagram-Stories): Die Karte richtet sich nach der FENSTERHOEHE und laesst
 * oben und unten nur einen schmalen Rand. Vorher war sie fest 380 px breit
 * (676 px hoch) und stand auf grossen Monitoren als kleines Handy in der Mitte
 * (5K bei 3200x1800: 40 % der Hoehe).
 *
 * Deckel 600 px Breite: Die Slides waehlen ihr Layout nach der BUEHNENBREITE
 * (seit 30.09.2026 = Kartenbreite, keine Blende mehr). Ab 615 px (slidePositionBreakpoints.iframe)
 * schrumpfen Vorlagen-Textboxen auf 280 px, ab 850 px (viewer-schmal) kippen
 * Pro-Editor-Scrollys in die Desktop-Fassung — gemessen an alphabeta/scrolly-
 * luma bei 911 px Karte: Schrift riesig und rechts abgeschnitten. Mit 600 px
 * bleibt die Buehne sicher im Handy-Layout.
 */
const KARTEN_RAND = 'clamp(20px, 2.5vh, 40px)';
const KARTEN_BREITE = `min(calc(100vw - 40px), calc((100vh - 2 * ${KARTEN_RAND}) * ${KARTEN_SEITENVERHAELTNIS}), 600px)`;

/**
 * Seiten-Scroll-Test (28.09.2026): Das Gehaeuse (Letterbox → Rahmen → Maske)
 * sperrt den Reader sonst in eine absolut positionierte Box mit overflow:
 * hidden und contain — die Seite haette keine Hoehe, und contain:paint
 * machte die Letterbox zum Bezugsrahmen der fixed Buehne (sie scrollte weg).
 * Im Seiten-Scroll fliesst alles ins Dokument. Der Vorhang steht auf 100svh
 * (Bildschirm MIT Leiste): der START-Knopf liegt nie unter der Leiste, und
 * die Hoehe springt nicht, wenn die Leiste einklappt.
 */
const SEITENSCROLL_REGELN = `
  .scrolly-shell--seitenscroll .scrolly-shell-letterbox {
    display: block !important; overflow: visible !important; contain: none !important;
  }
  .scrolly-shell--seitenscroll .scrolly-shell-frame {
    height: auto !important; overflow: visible !important;
  }
  .scrolly-shell--seitenscroll .scrolly-shell-mask {
    position: relative !important; inset: auto !important; overflow: visible !important;
  }
  .scrolly-shell--seitenscroll .scrolly-phone-mask [data-testid="cover-section"] {
    height: 100svh !important;
  }
  /* KEIN NACHFEDERN am Seitenende (Benedikts iPhone-Fund 28.09.2026): Safari
     liess die ganze Seite samt Footer ueber das Ende hinaus hochziehen. Die
     Buehne steht fixed am Bildschirm und bleibt dabei stehen, also schaute
     sie unter dem Footer hervor. Eigentlich steht "none" seit Mai im
     Seiten-Stylesheet (renderer/+onRenderHtml.tsx) — aber im Seiten-Scroll
     entsperrt der Player beim START das html-Element und setzt dort inline
     "contain" (useBlockVertScroll). Das schlug die alte Regel; contain
     verhindert nur das Weiterreichen der Geste, nicht das Federn. Daher hier
     !important. */
  html:has(.scrolly-shell--seitenscroll),
  html:has(.scrolly-shell--seitenscroll) body {
    overscroll-behavior: none !important;
  }
  /* NATIVER SEITEN-SCROLLBALKEN AUS (Jans iPhone-Fund 12.10.2026, „zwei
     Scrollbalken links und rechts"): Im Seiten-Scroll scrollt das DOKUMENT —
     iOS zeigte rechts seinen Overlay-Indikator ZUSAETZLICH zur eigenen
     Echo-Pille links. Das scrollbarWidth:'none' des Container-Scrolls sitzt
     am im Seiten-Scroll funktionslosen Wrapper. Safari versteht
     scrollbar-width seit 18.2 (iOS 18.2+); aeltere WebKits gehen ueber
     ::-webkit-scrollbar und behalten schlimmstenfalls den Indikator. */
  html:has(.scrolly-shell--seitenscroll) {
    scrollbar-width: none !important;
  }
  html:has(.scrolly-shell--seitenscroll)::-webkit-scrollbar,
  html:has(.scrolly-shell--seitenscroll) body::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
`;

/**
 * Messanzeige fuer den Seiten-Scroll-Test, nur mit `&hud=1`. Zeigt die Werte,
 * an denen der Juni-Fehler haengen wuerde: Scroll-Position, Seitenhoehe und
 * die drei Bildschirmhoehen. Ein Rueckwaerts-Sprung >= 200 px in einem Schritt
 * wird mit Vorher/Nachher festgehalten (echte Gesten scrollen in kleinen
 * Schritten).
 */
function SeitenScrollHud() {
  const [an, setAn] = useState(false);
  const [zeile, setZeile] = useState('');
  const [spruenge, setSpruenge] = useState<string[]>([]);
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has('hud')) return;
    setAn(true);
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;top:0;left:0;width:1px;height:100lvh;visibility:hidden;pointer-events:none';
    document.body.appendChild(probe);
    let letzterY = window.scrollY;
    let letzteH = document.documentElement.scrollHeight;
    let raf = 0;
    const tick = () => {
      const y = Math.round(window.scrollY);
      const h = document.documentElement.scrollHeight;
      const ih = window.innerHeight;
      const vv = Math.round(window.visualViewport?.height ?? 0);
      const lvh = Math.round(probe.getBoundingClientRect().height);
      setZeile(`y ${y} / max ${h - ih} · inner ${ih} · vv ${vv} · lvh ${lvh}`);
      if (y - letzterY <= -200) {
        // Text JETZT bauen: der State-Updater laeuft spaeter, dann ist
        // letzterY schon ueberschrieben.
        const eintrag = `${new Date().toISOString().slice(11, 19)} ${Math.round(letzterY)}→${y} (Hoehe ${letzteH}→${h}, inner ${ih})`;
        setSpruenge((alt) => [eintrag, ...alt].slice(0, 5));
      }
      letzterY = y;
      letzteH = h;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      probe.remove();
    };
  }, []);
  if (!an) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 4px)',
        left: 4,
        zIndex: 99999,
        background: 'rgba(0,0,0,0.75)',
        color: '#0f0',
        font: '10px/1.3 ui-monospace, monospace',
        padding: '4px 6px',
        borderRadius: 4,
        pointerEvents: 'none',
        maxWidth: 'calc(100vw - 80px)',
      }}
    >
      <div>{zeile}</div>
      {spruenge.map((z) => (
        <div key={z} style={{ color: '#f66' }}>
          Sprung {z}
        </div>
      ))}
    </div>
  );
}

/** Vollbild-Regeln der Shell — einmal fuer die Media-Query, einmal fuer die JS-Klasse. */
function vollbildRegeln(praefix: string): string {
  return `
    ${praefix}.scrolly-phone-mask [data-testid="cover-section"] { height: 100dvh !important; }
    ${praefix}.scrolly-shell-letterbox { padding: 0 !important; }
    ${praefix}.scrolly-shell-sharebg { display: none !important; }
    ${praefix}.scrolly-shell-frame {
      width: 100% !important;
      height: 100% !important;
      max-height: none !important;
      aspect-ratio: auto !important;
      border-radius: 0 !important;
      padding: 0 !important;
      box-shadow: none !important;
      transform: none !important;
    }
    ${praefix}.scrolly-shell-mask { inset: 0 !important; border-radius: 0 !important; --scrolly-vorhang-radius: 0px !important; }
  `;
}

function TopBar({
  closeUrl,
  modus,
  onToggleMode,
}: {
  closeUrl?: string;
  /** Gesetzt = Umschalter zeigen; Wert = aktueller Modus. */
  modus?: ViewerMode;
  onToggleMode?: () => void;
}) {
  return (
    <div
      style={{
        // Chrome-Scroll-Fix (2026-06-03): fixed statt absolute. Das Chrome ist
        // ans Return-Ende (außerhalb contain:content-Outer-Div) gehoben, daher
        // fixiert sich fixed am visual Viewport → klebt beim Scrollen, driftet nicht.
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        // 9999, nicht 15 (Benedikts iPhone-Fund 01.10.2026, Prod): Im Seiten-
        // Scroll faellt das contain des Gehaeuses weg, Buehne (z-998),
        // Textboxen (z-1000) und Cover (z-1002) stapeln dann im selben
        // Stapel wie diese Leiste und lagen ueber dem X — kein Schliessen
        // auf dem iPhone. Gleiche Stufe wie Org-Logo/Scroll-Pille (fc7b1ac5),
        // weiter unter dem Anmelde-Dialog (Z.modal 10000).
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        // X nach RECHTS (Jans Vorgabe 08.08.2026) — links kollidierte er
        // optisch mit dem Lese-Einstieg; Schliessen sitzt bei Overlays
        // gelernt oben rechts.
        justifyContent: 'flex-end',
        // Fix G (Safe-Area Top): Hintergrund-Gradient zieht bis top:0 unter den
        // Notch, der X-Button sitzt darunter. Fallback 0px → Non-Notch unverändert.
        padding: 'calc(env(safe-area-inset-top, 0px) + 20px) 28px 20px',
        // Sanfter Gradient, dezent (vorher rgba(0,0,0,0.6))
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.35), transparent)',
        pointerEvents: 'none',
      }}
    >
      {modus && onToggleMode && (
        <button
          type="button"
          onClick={onToggleMode}
          aria-label={modus === 'phone' ? t('viewer.fullscreen', LANG) : t('viewer.phoneView', LANG)}
          title={modus === 'phone' ? t('viewer.fullscreen', LANG) : t('viewer.phoneView', LANG)}
          data-testid="viewer-mode-toggle"
          style={{ ...iconButtonStyle, pointerEvents: 'auto', marginRight: 10 }}
        >
          {modus === 'phone' ? <IconMaximize size={20} /> : <IconMinimize size={20} />}
        </button>
      )}
      <button
        type="button"
        onClick={() => handleClose(closeUrl)}
        aria-label={t('viewer.close', LANG)}
        style={{ ...iconButtonStyle, pointerEvents: 'auto' }}
      >
        <IconX size={20} />
      </button>
    </div>
  );
}

// Sprint-2 helper: bündelt Colophon + UpNextInline für afterContentSlot.
// Mockup-Source: docs/mockups/viewer/Scrolly_Platform-2.html:1591-1601
// (ScrollyContent rendert sections → Colophon → UpNextInline).
//
// Block 2.6 P1 (2026-05-22): umbenannt PhoneAfterContent → AfterContent,
// nimmt `wide`-Prop. Wird in Phone-Mode (wide=false) UND Immersive-Mode
// (wide=true) verwendet. Vorlage Z. 1591-1601: ScrollyContent rendert
// sections + Colophon + UpNextInline; `wide` wird durch Phone/Immersive
// gesetzt.
function AfterContent({
  scrolly,
  wide,
  bodyFontFamily,
  engagementRow,
}: {
  scrolly: MockScrolly;
  wide: boolean;
  /** Org-Branding-Fließtextschrift (Weser Sans) für die reduzierte Fußzeile. */
  bodyFontFamily?: string;
  /** Like/Save/Share-Zeile am Scrolly-Ende (EngagementEnde, Jans Ansage
   *  24.08.2026) — Papier-Band direkt über dem Colophon. */
  engagementRow?: React.ReactNode;
}) {
  // Plan-N2 Block 3 (2026-05-25 Bene): Mobile-Schmalviewport-Detection
  // direkt im AfterContent. useResponsiveViewer ist ein einfacher
  // matchMedia-Hook, mehrfach-Aufruf ist nebenwirkungsfrei (kein State
  // außer dem media-query-listener). Vermeidet Prop-Drilling via
  // ScrollyContainer/ScrollyViewerHost, das wäre 3 Layer tief.
  const { isCompact } = useResponsiveViewer(undefined, true); // hydrationSafe (Fix #418)

  const post = scrolly.postJson?.data as
    | { metadata?: { credits?: string; sources?: string; contact?: string; aiDisclosure?: boolean } }
    | undefined;
  const metadata = post?.metadata ?? null;

  // Org-Branding-Flag (badges.showColophon===false): redaktionelle End-Slide aus
  // (Byline/„End."-Karte, Kontakt-Block, „powered by scrolly"), z.B.
  // WESER-KURIER. ABER: das gesetzlich verpflichtende Impressum/Datenschutz muss
  // bleiben — sonst ist der standalone erreichbare Viewer angreifbar
  // (Bene 2026-08-07). Daher schlanke Rechts-Fußzeile statt `return null`.
  // Bildnachweise/Quellen bleiben, falls im Scrolly gepflegt (Urheberbenennung).
  // Hooks laufen davor, daher ist dieser Early-Return React-konform.
  if (scrolly.showColophon === false) {
    return (
      <div style={{ position: 'relative', zIndex: 1300 }}>
        {engagementRow}
        <ColophonLegalFooter
          orgContact={scrolly.orgContact}
          legalLinks={scrolly.legalLinks}
          publishedIso={scrolly.publishedIso}
          credits={metadata?.credits}
          sources={metadata?.sources}
          wide={wide}
          isCompact={isCompact}
          lang={LANG}
          fontFamily={bodyFontFamily}
          showPoweredBy={scrolly.showPoweredBy !== false}
          aiDisclosure={metadata?.aiDisclosure === true}
        />
      </div>
    );
  }

  // Block 2.5 TEIL B (2026-05-22): Inline-Colophon (Profil-Karte mit
  // Avatar/Follow-Button/Bio + Lese-Fortschritt-Notice) entfällt; ersetzt
  // durch Vorlage-konforme ColophonSubtle/ColophonBold aus Colophon.tsx.
  // StoryFooter.tsx ebenfalls entfällt (war alter "powered by scrolly"-
  // Baustein, nicht Vorlage). Abbinder ist Teil des neuen Colophon.
  //
  // Block 2.5 TEIL C (2026-05-22): UpNextInline-Items-Refactor.
  // VORHER: UpNextInline las ALL_SCROLLYS-Mock (Dummy-Daten).
  // JETZT:  items={[]} — Rail wird heute ausgeblendet weil kein
  //         Public-Listing-Endpoint für Org-Nachbarn existiert.
  // Briefing 2026-05-22 (BRIEFING-BLOCK-2.5-END-SEQUENZ-UND-FULLSCREEN.md
  // TEIL C): "Wenn keine Nachbarn vorhanden: Rail leer lassen oder
  // ausblenden, NICHT mit Dummy füllen." Plan-2-Backlog: Public-Endpoint
  // `GET /public/scrollys/by-org/:orgSlug` + `useOrgNachbarn`-Hook
  // (siehe UpNextInline.tsx Header-Comment).
  return (
    // Fix (2026-06-13): Der Renderer-Sticky-Canvas (zIndex 998) + die fixed
    // Credits (zIndex 1200, packages/renderer VisualStoryPlayer) liegen im
    // SELBEN Wrapper-Stacking-Context wie diese Fußzeile. Ohne eigenen z-index
    // (= auto/0) malt das eingefrorene letzte Renderer-Frame am Story-Ende ÜBER
    // die Colophon-Karte ("Riesen-cr überlappt End.", Bene 2026-06-13). Gleiche
    // Klasse wie der CompanyLogo-z-2000-Fix (siehe lib/z-index.ts:30-38). 1300
    // deckt 998+1200 ab, bleibt unter Logo (2000) und Modal (10000).
    <div style={{ position: 'relative', zIndex: 1300 }}>
      {engagementRow}
      <Colophon
        creatorName={scrolly.creator.name}
        publishedAt={scrolly.publishedAt}
        publishedIso={scrolly.publishedIso}
        duration={scrolly.stats.duration}
        views={scrolly.stats.views ? String(scrolly.stats.views) : undefined}
        credits={metadata?.credits}
        sources={metadata?.sources}
        contact={metadata?.contact}
        aiDisclosure={metadata?.aiDisclosure === true}
        orgContact={scrolly.orgContact}
        legalLinks={scrolly.legalLinks}
        wide={wide}
        isCompact={isCompact}
        style="subtle"
        lang={LANG}
        showPoweredBy={scrolly.showPoweredBy !== false}
      />
      <UpNextInline items={[]} wide={wide} lang={LANG} />
    </div>
  );
}

function PlaceholderContent({ scrolly }: { scrolly: MockScrolly }) {
  const label =
    scrolly.source === 'imported'
      ? `Imported Scrolly · ${scrolly.importedFrom ?? 'Externe Quelle'}`
      : 'Scrolly-Content rendert hier';
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        color: 'rgba(255,255,255,0.72)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 500,
          color: '#fff',
          lineHeight: 1.3,
        }}
      >
        {scrolly.title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'rgba(255,255,255,0.5)',
          marginTop: 16,
          maxWidth: 280,
        }}
      >
        Der eigentliche Renderer folgt in einer späteren Phase. Der Block-4-
        Chrome zeigt Letterbox, Rail und Colophon-Gerüst.
      </div>
    </div>
  );
}

// ---- SideActions (Floating) --------------------------------------------
// EngagementEnde (Jans Ansage 24.08.2026): ehemals SideActions, die
// Floating-Rail unten rechts (davor RightRail, Sub-Step 3.4). Die Rail
// verdeckte Inhalte und belohnte nichts — jetzt steht die Zeile am ENDE
// des Scrollys als Teil des Rückumschlags (Papier-Band über dem
// Colophon): Wer durchgescrollt hat, kann liken, merken, teilen.
//
// Visual-Spec:
// - horizontales Band, Papier #f7f5ef (wie Colophon subtle/LegalFooter)
// - zentriert, gap:36, 52×52 runde Buttons in der `light`-Variante
// - Active-Colors: Pink für Heart, Yellow #FBBF24 für Bookmark
//
// Sub-Step 3.3 Phase 2: Counter immer sichtbar (auch anonym). Heart/
// Bookmark-State kommt aus `engagement.hasLiked`/`hasSaved` (nur bei
// auth-Request gesetzt). Anonyme User: Outlined-Icons + Click triggert
// onRequireSignIn (SignInModal). Authenticated User: Filled-Icons wenn
// liked/saved, Click toggled via useEngagementMutation mit optimistic
// update + rollback bei Fail.

function EngagementEnde({
  scrolly,
  loggedIn,
  onRequireSignIn,
  publicKey,
  engagement,
}: {
  scrolly: MockScrolly;
  loggedIn: boolean;
  onRequireSignIn: () => void;
  publicKey?: string;
  engagement?: EngagementCounts;
}) {
  // useEngagementMutation: lokaler State (initial aus engagement-Prop)
  // + Toggle-Funktionen für authentifizierte User. No-op-Toggles wenn
  // !loggedIn — Modal-Trigger wird oben im onClickAction-Handler gemacht.
  // useRef-Guard (Phase-1-Lesson): inFlightRef im Hook verhindert
  // konkurrierende Mutations bei Doppel-Klick.
  const mutation = useEngagementMutation({
    publicKey: publicKey ?? '',
    initialLiked: engagement?.hasLiked ?? false,
    initialSaved: engagement?.hasSaved ?? false,
    initialLikesCount: engagement?.likesCount ?? 0,
    initialSavesCount: engagement?.savesCount ?? 0,
  });

  // Counter werden IMMER angezeigt (Sub-Auftrag-Brief Strategy-Decision).
  // Fallback "0" wenn engagement-Field fehlt (mock-Pfade ohne Backend).
  const likeCount = mutation.state.likesCount;
  const saveCount = mutation.state.savesCount;
  // Anonyme User sehen mock-fallback aus `scrolly.stats` wenn engagement
  // fehlt — sonst die echten Backend-Counts (auch 0).
  const showLikeCount =
    engagement !== undefined ? likeCount : (scrolly.stats?.likes ?? 0);
  const showSaveCount =
    engagement !== undefined ? saveCount : (scrolly.stats?.saves ?? 0);

  const onLikeClick = () => {
    if (!loggedIn) return onRequireSignIn();
    void mutation.toggleLike();
  };
  const onSaveClick = () => {
    if (!loggedIn) return onRequireSignIn();
    void mutation.toggleSave();
  };
  // Lauras Wunsch 08.09.2026 (Weser-Kurier): nur Teilen, ohne Like und
  // Speichern. Org-Flag badges.showLikeSave=false.
  const { likeSave: zeigeLikeSave } = engagementVisibility(scrolly);

  // Bene 2026-06-13: Mobile zeigt jetzt dieselbe Einzel-Leiste wie das
  // Platform-Overlay (Herz/Merken/Teilen mit Zählern) — statt des bisherigen
  // 3-Punkte-Menüs (kehrt die Plan-N2-Block-3-v3-Entscheidung vom 25.05. um).
  // Die Rechts/Unten-Platzierung kollidiert nicht mit Cover-Titel/START (beide
  // links). Auf Mobile wird zIndex über die Renderer-Layer (998/1200) gehoben,
  // sonst verschwindet die Leiste hinter Slide-Inhalten — gleiche Lektion wie
  // CompanyLogo z-2000 / Colophon z-1300 (lib/z-index.ts:30-38).
  // Papier-Band am Scrolly-Ende: gleiche Fläche wie der folgende Colophon
  // (#f7f5ef), damit Zeile + Abspann als EIN Rückumschlag wirken.
  return (
    <div
      style={{
        background: '#f7f5ef',
        display: 'flex',
        flexDirection: 'row',
        gap: 36,
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '44px 24px 4px',
      }}
    >
      {zeigeLikeSave && (
        <>
          <ReactionBtn
            light
            aria-label={
              mutation.state.liked ? t('viewer.unlike', LANG) : t('viewer.like', LANG)
            }
            aria-pressed={mutation.state.liked}
            active={mutation.state.liked}
            activeColor={BRAND_VAR.accent}
            onClick={onLikeClick}
            icon={<IconHeart size={22} filled={mutation.state.liked} />}
            count={formatViews(showLikeCount)}
          />
          <ReactionBtn
            light
            aria-label={
              mutation.state.saved ? t('viewer.unsave', LANG) : t('viewer.save', LANG)
            }
            aria-pressed={mutation.state.saved}
            active={mutation.state.saved}
            activeColor="#FBBF24"
            onClick={onSaveClick}
            icon={<IconBookmark size={22} />}
            count={formatViews(showSaveCount)}
          />
        </>
      )}
      <ReactionBtn
        light
        aria-label={t('viewer.share', LANG)}
        active={false}
        // Mobile bevorzugt das native Share-Sheet (wie das Platform-Overlay);
        // Fallback Clipboard auf Desktop / ohne Web-Share-API.
        onClick={() => shareScrolly(scrolly.title)}
        icon={<IconShare size={22} />}
        // Label unter Share-Icon (kein Engagement-Counter). Sub-Step
        // 3.6.5 Befund-3-Fix.
        count="Share"
      />
    </div>
  );
}

function ReactionBtn({
  icon,
  count,
  active,
  activeColor,
  onClick,
  light = false,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
}: {
  icon: React.ReactNode;
  count?: string;
  active: boolean;
  activeColor?: string;
  onClick: () => void;
  /** Papier-Variante fürs EngagementEnde-Band (dunkle Icons auf #f7f5ef). */
  light?: boolean;
  'aria-label': string;
  'aria-pressed'?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          border: 'none',
          background: light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.12)',
          backdropFilter: light ? undefined : 'blur(14px)',
          WebkitBackdropFilter: light ? undefined : 'blur(14px)',
          color: active && activeColor ? activeColor : light ? '#1a1a1a' : '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 150ms',
          fontFamily: 'inherit',
        }}
      >
        {icon}
      </button>
      {count && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: light ? 'rgba(26,26,26,0.75)' : '#fff',
            textShadow: light ? undefined : '0 1px 3px rgba(0,0,0,0.4)',
          }}
        >
          {count}
        </div>
      )}
    </div>
  );
}

// ---- Shared helpers ----------------------------------------------------
